/**
 * `<DeleteAccountDialog/>` — GDPR Article 17 delete-my-data flow.
 *
 * Calls `DELETE /api/account/me` (Phase 12 enforces the soft-delete +
 * audit-log + cascade rules server-side). Requires the user to type
 * the literal string "delete" as a confirmation guard so a stray
 * click can't trigger the destructive path.
 *
 * On success we sign the user out of the SPA and bounce them to the
 * sign-in page — the dev-shim handles this synchronously; in Clerk
 * mode we additionally call `clerkSignOut` to clear the session
 * cookie.
 */
import { Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import { useAuth } from "../lib/AuthContext";
import { afterSignOutPath } from "../lib/accountSwitch";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "./ui";

export function DeleteAccountDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { signOut, authHeader, accounts } = useAuth();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (confirm.trim().toLowerCase() !== "delete") {
      setError("Type 'delete' to confirm.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const headers = await authHeader();
      await apiFetch("/api/account/me", { method: "DELETE", headers });
      // Await the revoke — firing it and navigating in the same tick aborts
      // the request and leaves the session alive (#1520). Its own try/catch
      // because the account is already gone by this point: a failed revoke
      // must not re-open the error path and read as "delete failed".
      // #3005 — sign out of the deleted account only: in multi-session
      // mode a bare `signOut()` would end every account on the browser.
      // Choose an account opens while another account stays signed in.
      const target = afterSignOutPath(accounts, "current", "/sign-in");
      try {
        await signOut({ redirectUrl: target, scope: "current" });
      } catch {
        // Fall through to the client-side redirect below.
      }
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-danger">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Delete your account
          </DialogTitle>
          <DialogDescription>
            We'll soft-delete your account: PII fields are nulled, future-cycle submissions are
            removed, past-cycle submissions are anonymised in place, and your memberships move to{" "}
            <code className="font-mono">left</code>. The team's audit log retains the action — leads
            will see "this account was deleted" in their audit trail.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm-delete">
              Type <span className="font-mono text-danger">delete</span> to confirm
            </Label>
            <Input
              id="confirm-delete"
              autoFocus
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy}
            />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={busy}>
              {busy ? "Deleting…" : "Delete my account"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
